/* Lecture des données depuis le Google Sheet (onglets "Coureurs" et "Evenements").
   Si le Sheet n'est pas accessible ou qu'un onglet est vide, on utilise les données
   locales existantes (src/store/success.ts et src/store/activities.ts). */
import {useEffect, useState} from "react";
import {SHEET_ID, SHEET_TABS} from "../config";
import success from "../store/success";
import activities from "../store/activities";

export interface Rider {
    firstName: string;
    lastName: string;
    category: string;
    year: string;
    club: string;
    photo: string;
    bio: string;
    juniorMedals: string;
    worldMedals: string;
    wcWins: string;
    globes: string;
    results: string[];
    fisCode: string;
    instagram: string;
}

export interface ClubEvent {
    start: Date;
    end: Date;
    title: string;
    place: string;
    type: string;
    description: string;
    link: string;
    linkText: string;
    image: string;
    featured: boolean;
}

/* ---------- CSV ---------- */
export function parseCSV(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [], cur = "", q = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (q) {
            if (c === '"' && text[i + 1] === '"') { cur += '"'; i++; }
            else if (c === '"') q = false;
            else cur += c;
        } else if (c === '"') q = true;
        else if (c === ",") { row.push(cur); cur = ""; }
        else if (c === "\n" || c === "\r") {
            if (c === "\r" && text[i + 1] === "\n") i++;
            row.push(cur); rows.push(row); row = []; cur = "";
        } else cur += c;
    }
    if (cur || row.length) { row.push(cur); rows.push(row); }
    return rows.filter(r => r.some(v => v.trim() !== ""));
}

// "Date début" -> "datedebut"
const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");
type Row = Record<string, string>;

function toObjects(rows: string[][]): Row[] {
    if (!rows.length) return [];
    const [head, ...body] = rows;
    const keys = head.map(norm);
    return body.map(r => Object.fromEntries(keys.map((k, i) => [k, (r[i] || "").trim()])));
}

const pick = (o: Row, ...keys: string[]) => { for (const k of keys) if (o[k]) return o[k]; return ""; };
const yes = (v: string) => /^(oui|ja|yes|x|1|true|vrai)$/i.test(v || "");

export async function loadTab(name: string): Promise<Row[]> {
    const url = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:csv&headers=1&sheet=${encodeURIComponent(name)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Sheet ${name}: ${res.status}`);
    const text = await res.text();
    if (text.trim().startsWith("<")) throw new Error(`Sheet ${name}: pas partagé publiquement`);
    return toObjects(parseCSV(text));
}

/* ---------- dates / images ---------- */
export function parseDate(v: string): Date | null {
    if (!v) return null;
    let m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    m = v.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/); // 30.01.2027 ou 30/01/2027
    if (m) return new Date(m[3].length === 2 ? 2000 + +m[3] : +m[3], +m[2] - 1, +m[1]);
    const d = new Date(v);
    return isNaN(+d) ? null : d;
}

// Un lien Google Drive ("partager") devient une image affichable
export function imageUrl(u: string): string {
    if (!u) return "";
    const m = u.match(/drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?id=)([\w-]+)/);
    if (m) return `https://lh3.googleusercontent.com/d/${m[1]}=w1600`;
    if (/^(https?:)?\/\//.test(u) || u.startsWith("/")) return u;
    return process.env.PUBLIC_URL + "/" + u; // chemin dans public/, ex. images/lea.jpg
}

/* ---------- mapping ---------- */
function toRider(r: Row): Rider {
    return {
        firstName: pick(r, "prenom", "vorname", "firstname"),
        lastName: pick(r, "nom", "name", "lastname"),
        category: pick(r, "categorie", "kategorie", "category", "groupe"),
        year: pick(r, "annee", "anneedenaissance", "naissance", "jahrgang", "year"),
        club: pick(r, "club", "skiclub"),
        photo: imageUrl(pick(r, "photo", "image", "foto", "bild")),
        bio: pick(r, "bio", "description", "presentation"),
        juniorMedals: pick(r, "medaillesjuniors", "medaillesjunior", "juniors"),
        worldMedals: pick(r, "medaillesmondiaux", "mondiaux", "worldmedals"),
        wcWins: pick(r, "victoirescm", "victoires", "wcwins"),
        globes: pick(r, "globes", "globe"),
        results: pick(r, "resultats", "palmares", "results").split(/\n|;/).map(s => s.trim()).filter(Boolean),
        fisCode: pick(r, "codefis", "fiscode", "fis", "lienfis"),
        instagram: pick(r, "instagram", "insta"),
    };
}

function toEvent(e: Row): ClubEvent | null {
    const start = parseDate(pick(e, "datedebut", "date", "debut", "start"));
    const title = pick(e, "titre", "evenement", "nom", "title");
    if (!start || !title) return null;
    return {
        start,
        end: parseDate(pick(e, "datefin", "fin", "end")) || start,
        title,
        place: pick(e, "lieu", "ort", "station", "location"),
        type: pick(e, "type", "categorie", "discipline"),
        description: pick(e, "description", "infos", "info"),
        link: pick(e, "lien", "url", "link"),
        linkText: pick(e, "textedulien", "textelien", "linktext"),
        image: imageUrl(pick(e, "image", "photo", "bild")),
        featured: yes(pick(e, "miseenavant", "avant", "featured", "vedette")),
    };
}

/* ---------- données locales de secours ---------- */
const fallbackRiders: Rider[] = success.map(s => {
    const [firstName, ...rest] = s.title.split(" ");
    return {
        firstName, lastName: rest.join(" "), category: "", year: "", club: "", photo: imageUrl(s.img), bio: "",
        juniorMedals: s.text, worldMedals: s.text2, wcWins: s.text3, globes: s.text4, results: [], fisCode: "", instagram: "",
    };
});

const fallbackEvents: ClubEvent[] = activities.map(a => {
    const start = parseDate(a.date)!;
    return {
        start, end: parseDate(a.end || "") || start, title: a.title, place: a.place || "", type: a.text2, description: a.text,
        link: a.link, linkText: a.linkText, image: imageUrl(a.img), featured: !!a.featured,
    };
});

/* ---------- hook React (une seule requête par onglet, partagée) ---------- */
const cache: Record<string, Promise<Row[]>> = {};
const getTab = (name: string) => (cache[name] ??= loadTab(name).catch(err => { console.warn(err); return []; }));

export interface SheetState<T> { data: T[]; loading: boolean; fromSheet: boolean; }

function useTab<T>(tab: string, map: (r: Row) => T | null, fallback: T[]): SheetState<T> {
    const [state, setState] = useState<SheetState<T>>({data: fallback, loading: true, fromSheet: false});
    useEffect(() => {
        let alive = true;
        getTab(tab).then(rows => {
            if (!alive) return;
            const data = rows.map(map).filter(Boolean) as T[];
            setState(data.length ? {data, loading: false, fromSheet: true} : {data: fallback, loading: false, fromSheet: false});
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);
    return state;
}

export const useRiders = () => useTab<Rider>(SHEET_TABS.coureurs, r => {
    if (/^(non|nein|no|0)$/i.test(pick(r, "actif", "aktiv", "active"))) return null;
    const rider = toRider(r);
    return rider.firstName || rider.lastName ? rider : null;
}, fallbackRiders);

export const useEvents = () => {
    const s = useTab<ClubEvent>(SHEET_TABS.evenements, toEvent, fallbackEvents);
    return {...s, data: [...s.data].sort((a, b) => +a.start - +b.start)};
};

export const today = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
